import { useEffect, useState } from 'react';
import { Layout } from '../components/Layout';
import { companiesAPI, contractTypesAPI } from '../lib/api';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import {
  Plus,
  Pencil,
  Trash2,
  Building2,
  Bookmark,
  Loader2,
  Search,
  ArrowUpDown,
  List
} from 'lucide-react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

const companySchema = z.object({
  name: z.string().min(1, 'Company name is required'),
  address: z.string().optional(),
  phone: z.string().optional(),
  email: z.string().email('Enter a valid email').optional().or(z.literal('')),
  status: z.boolean().default(true),
});

const contractTypeSchema = z.object({
  name: z.string().min(1, 'Type name is required'),
  description: z.string().optional(),
  company_id: z.string().min(1, 'Company is required'),
  status: z.boolean().default(true),
});

export default function ContractSettings() {
  const { t } = useTranslation();
  const [companies, setCompanies] = useState([]);
  const [contractTypes, setContractTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [idSortAsc, setIdSortAsc] = useState(false);
  const [isCompanyModalOpen, setIsCompanyModalOpen] = useState(false);
  const [isTypeModalOpen, setIsTypeModalOpen] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [editingType, setEditingType] = useState(null);
  const [saving, setSaving] = useState(false);
  const [selectedInitialContractTypeName, setSelectedInitialContractTypeName] = useState('');
  const [showContractTypeError, setShowContractTypeError] = useState(false);
  const [isAddingNewType, setIsAddingNewType] = useState(false);

  const contractTypeNameOptions = Array.from(
    new Set(
      (contractTypes || [])
        .filter((t) => {
          const company = companies.find((c) => c.id === t.company_id);
          const isCompanyActive = company ? company.status !== false && company.status !== 0 : false;
          const isTypeActive = t.status !== false && t.status !== 0;
          return isCompanyActive && isTypeActive;
        })
        .map((t) => t?.name)
        .filter(Boolean)
    )
  ).sort((a, b) => a.localeCompare(b));

  const companyForm = useForm({
    resolver: zodResolver(companySchema),
    defaultValues: {
      status: true
    }
  });

  const typeForm = useForm({
    resolver: zodResolver(contractTypeSchema),
    defaultValues: {
      status: true
    }
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [companiesRes, typesRes] = await Promise.all([
        companiesAPI.getAll(),
        contractTypesAPI.getAll()
      ]);
      setCompanies(companiesRes.data);
      setContractTypes(typesRes.data);
    } catch (error) {
      toast.error(t('settings.loadError'));
    } finally {
      setLoading(false);
    }
  };

  // Company handlers
  const handleOpenCompanyModal = (company = null) => {
    if (company) {
      setEditingCompany(company);
      setSelectedInitialContractTypeName('');
      companyForm.reset({
        name: company.name,
        address: company.address || '',
        phone: company.phone || '',
        email: company.email || '',
        status: company.status !== undefined ? company.status : true,
      });
    } else {
      setEditingCompany(null);
      // Keep placeholder selected by default (user must pick)
      setSelectedInitialContractTypeName('');
      setShowContractTypeError(false);
      setIsAddingNewType(contractTypeNameOptions.length === 0);
      companyForm.reset({
        name: '',
        address: '',
        phone: '',
        email: '',
        status: true,
      });
    }
    setIsCompanyModalOpen(true);
  };

  const handleCloseCompanyModal = () => {
    setIsCompanyModalOpen(false);
    setEditingCompany(null);
    setSelectedInitialContractTypeName('');
    setShowContractTypeError(false);
    companyForm.reset();
  };

  const onSubmitCompany = async (data) => {
    setSaving(true);
    try {
      if (editingCompany) {
        // Backend uses full update, preserve fields not editable here
        const payload = {
          name: data.name,
          status: data.status,
          address: editingCompany.address ?? null,
          phone: editingCompany.phone ?? null,
          email: editingCompany.email ?? null,
        };
        await companiesAPI.update(editingCompany.id, payload);
        toast.success(t('settings.companyUpdated'));
      } else {
        // Validate that contract type is provided when adding new company
        const typeName = (selectedInitialContractTypeName || '').trim();
        if (!typeName) {
          setShowContractTypeError(true);
          toast.error(t('settings.contractTypeRequired'));
          setSaving(false);
          return;
        }

        const payload = {
          ...data,
          email: data.email || null,
        };
        // Add company; create first contract type (required)
        const createCompanyRes = await companiesAPI.create(payload);
        const createdCompanyId = createCompanyRes?.data?.id;
        if (createdCompanyId) {
          await contractTypesAPI.create({
            name: typeName,
            description: '',
            company_id: createdCompanyId,
            status: true,
          });
        }
        toast.success(t('settings.companyAdded'));
      }
      handleCloseCompanyModal();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('settings.operationFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCompany = async (id) => {
    if (!window.confirm(t('settings.confirmDeleteCompany'))) return;

    try {
      await companiesAPI.delete(id);
      toast.success(t('settings.companyDeleted'));
      fetchData();
    } catch (error) {
      toast.error(t('settings.companyDeleteError'));
    }
  };

  // Contract Type handlers
  const handleOpenTypeModal = (type = null) => {
    if (type) {
      setEditingType(type);
      typeForm.reset({
        name: type.name,
        description: type.description || '',
        company_id: type.company_id,
        status: type.status !== undefined ? type.status : true,
      });
    } else {
      setEditingType(null);
      // Keep placeholder selected by default (user must pick)
      const defaultCompanyId = '';
      typeForm.reset({
        name: '',
        description: '',
        company_id: defaultCompanyId,
        status: true,
      });
    }
    setIsTypeModalOpen(true);
  };

  const handleCloseTypeModal = () => {
    setIsTypeModalOpen(false);
    setEditingType(null);
    typeForm.reset();
  };

  const onSubmitType = async (data) => {
    setSaving(true);
    try {
      if (editingType) {
        // Backend uses full update, preserve fields not editable here
        const payload = {
          name: data.name,
          status: data.status,
          company_id: editingType.company_id,
          description: editingType.description ?? '',
        };
        await contractTypesAPI.update(editingType.id, payload);
        toast.success(t('settings.contractTypeUpdated'));
      } else {
        await contractTypesAPI.create({
          ...data,
          description: data.description || '',
          status: true,
        });
        toast.success(t('settings.contractTypeAdded'));
      }
      handleCloseTypeModal();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('settings.operationFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteType = async (id) => {
    if (!window.confirm(t('settings.confirmDeleteContractType'))) return;

    try {
      await contractTypesAPI.delete(id);
      toast.success(t('settings.contractTypeDeleted'));
      fetchData();
    } catch (error) {
      toast.error(t('settings.contractTypeDeleteError'));
    }
  };

  const getTableData = () => {
    const rows = [];
    const sortedCompanies = [...companies].sort((a, b) => a.name.localeCompare(b.name));

    sortedCompanies.forEach(company => {
      const types = contractTypes.filter(t => t.company_id === company.id);
      if (types.length > 0) {
        types.forEach(type => {
          if (
            searchQuery &&
            !company.name.toLowerCase().includes(searchQuery.toLowerCase()) &&
            !type.name.toLowerCase().includes(searchQuery.toLowerCase())
          ) {
            return;
          }
          rows.push({
            type: 'contract-type',
            company: company,
            contractType: type,
            key: `${company.id}-${type.id}`
          });
        });
      } else {
        if (searchQuery && !company.name.toLowerCase().includes(searchQuery.toLowerCase())) {
          return;
        }
        rows.push({
          type: 'company-only',
          company: company,
          contractType: null,
          key: `${company.id}-none`
        });
      }
    });

    const orphanedTypes = contractTypes.filter(t => !companies.find(c => c.id === t.company_id));
    orphanedTypes.forEach(type => {
      if (searchQuery && !type.name.toLowerCase().includes(searchQuery.toLowerCase())) return;
      rows.push({
        type: 'contract-type-orphaned',
        company: { id: 'unknown', name: 'Unknown' },
        contractType: type,
        key: `unknown-${type.id}`
      });
    });

    return rows;
  };

  const tableRows = getTableData();
  const displayRows = idSortAsc ? [...tableRows].reverse() : tableRows;

  if (loading) {
    return (
      <Layout>
        <div className="space-y-6">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-96 w-full" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="contract-settings-page">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold tracking-tight dark:text-white">{t('settings.contractSettingsTitle')}</h1>
          <p className="text-muted-foreground dark:text-gray-400 mt-1">
            {t('settings.subtitle')}
          </p>
        </div>

        {/* Stats and Search */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <p className="text-sm text-muted-foreground dark:text-gray-400">
            {t('settings.contractSettingsTotalRows', { count: tableRows.length })}
          </p>
          <div className="relative max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder={t('settings.searchPlaceholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 dark:bg-[#333333] dark:border-gray-700 dark:text-white dark:placeholder:text-gray-500"
              data-testid="search-input"
            />
          </div>
        </div>

        {/* Main Table */}
        <Card className="dark:bg-[#333333] dark:border-zinc-800 overflow-hidden">
          <CardContent className="p-0 overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="table-header dark:bg-[#444444] dark:border-gray-700">
                  <TableHead className="w-24 dark:text-white">
                    <div className="flex items-center justify-between gap-2">
                      <span>{t('settings.id')}</span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6"
                        onClick={() => setIdSortAsc((v) => !v)}
                        title={t('common.sort')}
                        data-testid="id-sort-btn"
                      >
                        <ArrowUpDown className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableHead>
                  <TableHead className="dark:text-white">
                    <div className="flex items-center gap-2">
                      <span className="inline-flex items-center gap-2">
                        <Building2 className="w-4 h-4 text-muted-foreground dark:text-gray-400" />
                        {t('settings.companyName')}
                      </span>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 bg-teal-100 hover:bg-teal-200 text-teal-700 dark:bg-emerald-900/40 dark:text-emerald-400 dark:hover:bg-emerald-900/60"
                        onClick={() => handleOpenCompanyModal()}
                        data-testid="add-company-btn"
                      >
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableHead>
                  <TableHead className="dark:text-white">
                    <div className="flex items-center gap-2">
                      <span className="inline-flex items-center gap-2">
                        <Bookmark className="w-4 h-4 text-muted-foreground dark:text-gray-400" />
                        {t('settings.contractType')}
                      </span>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 bg-teal-100 hover:bg-teal-200 text-teal-700 dark:bg-emerald-900/40 dark:text-emerald-400 dark:hover:bg-emerald-900/60"
                        onClick={() => handleOpenTypeModal()}
                        data-testid="add-type-btn"
                      >
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableHead>
                  <TableHead className="text-right dark:text-white">{t('settings.contractCount')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tableRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center py-12">
                      <Building2 className="w-12 h-12 mx-auto text-muted-foreground/50 dark:text-gray-600 mb-4" />
                      <p className="text-muted-foreground dark:text-gray-500">{t('settings.noRecordsYet')}</p>
                    </TableCell>
                  </TableRow>
                ) : (
                  displayRows.map((row, index) => (
                    <TableRow key={row.key} className="table-row dark:border-gray-700 dark:hover:bg-[#3a3a3a]">
                      <TableCell className="table-cell font-mono text-muted-foreground dark:text-gray-400">
                        {idSortAsc ? index + 1 : displayRows.length - index}
                      </TableCell>

                      <TableCell className="table-cell">
                        <div className="flex items-center gap-2">
                          <span className={(row.company.status === false || row.company.status === 0) ? "text-muted-foreground line-through dark:text-gray-500" : "font-medium dark:text-white"}>
                            {row.company.name}
                          </span>
                          {row.company.id !== 'unknown' && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6 dark:text-gray-400 dark:hover:text-white dark:hover:bg-gray-700"
                              onClick={() => handleOpenCompanyModal(row.company)}
                            >
                              <Pencil className="w-3 h-3" />
                            </Button>
                          )}
                        </div>
                      </TableCell>

                      <TableCell className="table-cell">
                        {row.contractType ? (
                          <div className="flex items-center gap-2">
                            <span className={(row.contractType.status === false || row.contractType.status === 0) ? "text-muted-foreground line-through dark:text-gray-500" : "dark:text-white"}>
                              {row.contractType.name}
                            </span>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6 dark:text-gray-400 dark:hover:text-white dark:hover:bg-gray-700"
                              onClick={() => handleOpenTypeModal(row.contractType)}
                            >
                              <Pencil className="w-3 h-3" />
                            </Button>
                          </div>
                        ) : (
                          <span className="text-muted-foreground dark:text-gray-500">-</span>
                        )}
                      </TableCell>

                      <TableCell className="table-cell text-right font-mono">
                        <span className={(row.contractType ? (row.contractType.status === false || row.contractType.status === 0) : (row.company.status === false || row.company.status === 0)) ? "text-muted-foreground line-through dark:text-gray-500" : "font-mono text-zinc-700 dark:text-gray-300"}>
                          {row.contractType ? (row.contractType.active_contract_count || 0) : (row.company.active_contract_count || 0)}
                        </span>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Company Modal */}
        <Dialog
          open={isCompanyModalOpen}
          onOpenChange={(open) => {
            if (!open) handleCloseCompanyModal();
            else setIsCompanyModalOpen(true);
          }}
        >
          <DialogContent className="sm:max-w-lg dark:bg-[#333333] dark:border-none dark:text-white" data-testid="company-modal">
            <DialogHeader>
              <DialogTitle className="dark:text-white">
                {editingCompany ? t('settings.editCompany') : t('settings.addNewCompany')}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={companyForm.handleSubmit(onSubmitCompany)} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name" className="dark:text-white">{t('settings.companyName')}</Label>
                <Input
                  id="name"
                  {...companyForm.register('name')}
                  placeholder={t('settings.newCompanyNamePlaceholder')}
                  data-testid="company-name-input"
                  className="dark:bg-[#444444] dark:border-gray-700 dark:text-white dark:placeholder:text-gray-500"
                />
                {companyForm.formState.errors.name && (
                  <p className="text-sm text-destructive">{companyForm.formState.errors.name.message}</p>
                )}
              </div>

              {!editingCompany && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="initialContractType" className="dark:text-white">
                      {t('settings.contractType')} <span className="text-destructive">*</span>
                    </Label>
                    {contractTypeNameOptions.length > 0 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-8 px-2 dark:text-gray-400 dark:hover:text-white"
                        onClick={() => setIsAddingNewType(!isAddingNewType)}
                        title={isAddingNewType ? t('settings.selectContractType') : t('settings.addNewType')}
                      >
                        {isAddingNewType ? <List className="w-4 h-4 mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
                        {isAddingNewType ? t('common.view') : t('common.add')}
                      </Button>
                    )}
                  </div>

                  {(!isAddingNewType && contractTypeNameOptions.length > 0) ? (
                    <select
                      id="initialContractTypeSelect"
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-[#444444] dark:border-gray-700 dark:text-white"
                      value={selectedInitialContractTypeName}
                      onChange={(e) => {
                        setSelectedInitialContractTypeName(e.target.value);
                        if (showContractTypeError && e.target.value) setShowContractTypeError(false);
                      }}
                      onBlur={() => {
                        if (!selectedInitialContractTypeName) setShowContractTypeError(true);
                      }}
                      data-testid="company-initial-contract-type-select"
                    >
                      <option value="" className="dark:bg-[#444444]">{t('settings.selectContractType')}</option>
                      {contractTypeNameOptions.map((name) => (
                        <option key={name} value={name} className="dark:bg-[#444444]">{name}</option>
                      ))}
                    </select>
                  ) : (
                    <Input
                      id="initialContractTypeInput"
                      value={selectedInitialContractTypeName}
                      onChange={(e) => {
                        setSelectedInitialContractTypeName(e.target.value);
                        if (showContractTypeError && e.target.value.trim()) {
                          setShowContractTypeError(false);
                        }
                      }}
                      onBlur={() => {
                        if (!selectedInitialContractTypeName.trim()) {
                          setShowContractTypeError(true);
                        }
                      }}
                      placeholder={t('settings.newContractTypeNamePlaceholder')}
                      data-testid="company-initial-contract-type-input"
                      className="dark:bg-[#444444] dark:border-gray-700 dark:text-white dark:placeholder:text-gray-500"
                      required
                    />
                  )}
                  {showContractTypeError && !selectedInitialContractTypeName.trim() && (
                    <p className="text-sm text-destructive">{t('settings.contractTypeRequired')}</p>
                  )}
                </div>
              )}

              {editingCompany && (
                <div className="flex items-center space-x-2">
                  <Controller
                    control={companyForm.control}
                    name="status"
                    render={({ field }) => (
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                        id="company-status"
                        className="dark:data-[state=unchecked]:bg-[#444444] dark:data-[state=checked]:bg-zinc-200"
                      />
                    )}
                  />
                  <Label htmlFor="company-status" className="dark:text-white">{t('settings.active')}</Label>
                </div>
              )}

              <DialogFooter>
                <Button
                  type="submit"
                  disabled={saving}
                  className="bg-emerald-700 hover:bg-emerald-800 text-white dark:bg-emerald-600 dark:hover:bg-emerald-500"
                  data-testid="company-save-btn"
                >
                  {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  {editingCompany ? t('common.update') : t('common.add')}
                </Button>
                <Button
                  type="button"
                  onClick={handleCloseCompanyModal}
                  className="bg-zinc-600 hover:bg-zinc-700 text-white dark:bg-gray-700 dark:hover:bg-gray-600"
                >
                  {t('common.close')}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Contract Type Modal */}
        <Dialog
          open={isTypeModalOpen}
          onOpenChange={(open) => {
            if (!open) handleCloseTypeModal();
            else setIsTypeModalOpen(true);
          }}
        >
          <DialogContent className="sm:max-w-lg dark:bg-[#333333] dark:border-none dark:text-white" data-testid="type-modal">
            <DialogHeader>
              <DialogTitle className="dark:text-white">
                {editingType ? t('settings.editContractType') : t('settings.addNewContractType')}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={typeForm.handleSubmit(onSubmitType)} className="space-y-4">
              {!editingType ? (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="companyId" className="dark:text-white">{t('settings.selectCompany')}</Label>
                    <select
                      id="companyId"
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-[#444444] dark:border-gray-700 dark:text-white"
                      {...typeForm.register('company_id')}
                      data-testid="type-company-select"
                    >
                      <option value="" className="dark:bg-[#444444]">{t('settings.selectCompanyPlaceholder')}</option>
                      {companies.filter(c => c.status !== false && c.status !== 0).map((c) => (
                        <option key={c.id} value={c.id} className="dark:bg-[#444444]">
                          {c.name}
                        </option>
                      ))}
                    </select>
                    {typeForm.formState.errors.company_id && (
                      <p className="text-sm text-destructive">{typeForm.formState.errors.company_id.message}</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="typeName" className="dark:text-white">{t('settings.contractTypeName')}</Label>
                    <Input
                      id="typeName"
                      {...typeForm.register('name')}
                      placeholder={t('settings.newContractTypeNamePlaceholder')}
                      data-testid="type-name-input"
                      className="dark:bg-[#444444] dark:border-gray-700 dark:text-white dark:placeholder:text-gray-500"
                    />
                    {typeForm.formState.errors.name && (
                      <p className="text-sm text-destructive">{typeForm.formState.errors.name.message}</p>
                    )}
                  </div>
                </>
              ) : (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="typeName" className="dark:text-white">{t('settings.contractTypeName')}</Label>
                    <Input
                      id="typeName"
                      {...typeForm.register('name')}
                      data-testid="type-name-input"
                      className="dark:bg-[#444444] dark:border-gray-700 dark:text-white"
                    />
                    {typeForm.formState.errors.name && (
                      <p className="text-sm text-destructive">{typeForm.formState.errors.name.message}</p>
                    )}
                  </div>

                  <div className="flex items-center space-x-2">
                    <Controller
                      control={typeForm.control}
                      name="status"
                      render={({ field }) => (
                        <Switch
                          checked={field.value}
                          onCheckedChange={field.onChange}
                          id="type-status"
                          className="dark:data-[state=unchecked]:bg-[#444444] dark:data-[state=checked]:bg-zinc-200"
                        />
                      )}
                    />
                    <Label htmlFor="type-status" className="dark:text-white">{t('settings.active')}</Label>
                  </div>
                </>
              )}

              <DialogFooter>
                <Button
                  type="submit"
                  disabled={saving}
                  className="bg-emerald-700 hover:bg-emerald-800 text-white dark:bg-emerald-600 dark:hover:bg-emerald-500"
                  data-testid="type-save-btn"
                >
                  {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  {editingType ? t('common.update') : t('common.add')}
                </Button>
                <Button
                  type="button"
                  onClick={handleCloseTypeModal}
                  className="bg-zinc-600 hover:bg-zinc-700 text-white dark:bg-gray-700 dark:hover:bg-gray-600"
                >
                  {t('common.close')}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
